import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Typography,
  Box,
  Divider
} from '@mui/material';

/**
 * PDF Report Options Dialog
 * 
 * Usage:
 * <PdfOptionsDialog 
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   assessmentId={assessmentId}
 *   onGenerate={(options) => handleGeneratePdf(options)}
 * />
 */
const PdfOptionsDialog = ({ open, onClose, assessmentId, onGenerate }) => {
  // State for checkbox selections
  const [includeMet, setIncludeMet] = useState(false);
  const [includeNotMet, setIncludeNotMet] = useState(true);
  const [includePartiallyMet, setIncludePartiallyMet] = useState(true);
  const [includeNA, setIncludeNA] = useState(false);
  
  // State for private notes option
  const [includePrivateNotes, setIncludePrivateNotes] = useState(false);
  
  // State for sort option
  const [sortBy, setSortBy] = useState('category');

  const handleGenerate = () => {
    const options = {
      include_met: includeMet,
      include_not_met: includeNotMet,
      include_partially_met: includePartiallyMet,
      include_na: includeNA,
      include_private_notes: includePrivateNotes,
      sort_by: sortBy
    };
    
    onGenerate(options);
    onClose();
  };

  const handleCancel = () => {
    // Reset to defaults
    setIncludeMet(false);
    setIncludeNotMet(true);
    setIncludePartiallyMet(true);
    setIncludeNA(false);
    setIncludePrivateNotes(false);
    setSortBy('category');
    onClose();
  };

  const hasSelections = includeMet || includeNotMet || includePartiallyMet || includeNA;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>PDF Report Options</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          All reports include the Cover Page and Executive Summary. 
          Select additional details to include:
        </Typography>

        <Box sx={{ mb: 3 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 1 }}>
              Include Detail Items:
            </FormLabel>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeNotMet}
                    onChange={(e) => setIncludeNotMet(e.target.checked)}
                    color="error"
                  />
                }
                label="Not Met (gaps requiring attention)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includePartiallyMet}
                    onChange={(e) => setIncludePartiallyMet(e.target.checked)}
                    color="warning"
                  />
                }
                label="Partially Met (areas needing improvement)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeMet}
                    onChange={(e) => setIncludeMet(e.target.checked)}
                    color="success"
                  />
                }
                label="Met (successfully implemented controls)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeNA}
                    onChange={(e) => setIncludeNA(e.target.checked)}
                  />
                }
                label="N/A (not applicable controls)"
              />
            </FormGroup>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 1 }}>
              Additional Options:
            </FormLabel>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includePrivateNotes}
                    onChange={(e) => setIncludePrivateNotes(e.target.checked)}
                    color="primary"
                  />
                }
                label="Include Private Assessor Notes (internal use only)"
              />
            </FormGroup>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, ml: 4 }}>
              Private notes are for internal documentation and should not be shared with clients unless explicitly intended.
            </Typography>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 1 }}>
              Sort Order:
            </FormLabel>
            <RadioGroup
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <FormControlLabel
                value="status"
                control={<Radio />}
                label="By Implementation Status (Not Met → Partially Met → Met → N/A)"
              />
              <FormControlLabel
                value="category"
                control={<Radio />}
                label="By Category/Control (grouped by security domain)"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        {!hasSelections && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Please select at least one detail category to include in the report.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleGenerate} 
          variant="contained" 
          color="primary"
          disabled={!hasSelections}
        >
          Generate Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PdfOptionsDialog;
